// The workspace's contact settings as GET /v1/contact-fields returns them
// (docs/design/contacts.md, Contact settings), and the names the app shows for
// a contact's class, stage, and grade keys.

export interface Lifecycle { key: string; name: string; stages: Array<{ key: string; name: string }> }
export interface ContactClass { key: string; name: string; description: string | null; lifecycle: string; is_default: boolean }
export interface Grade { key: string; label: string; description: string | null }

export interface Fields {
  columns: Array<{ key: string; label: string; type: string; sortable: boolean }>;
  filters: Array<{ field: string; label: string; type: string; ops: string[]; options: unknown }>;
  default_columns: string[];
  lifecycles: Lifecycle[];
  classes: ContactClass[];
  grades: Grade[];
}

export const NO_STAGE = 'No stage';

export const defaultClass = (f: Fields | null): ContactClass | undefined => f?.classes.find((c) => c.is_default);

export function lifecycleOf(f: Fields | null, contactClass: string | null | undefined): Lifecycle | undefined {
  const lc = f?.classes.find((c) => c.key === contactClass)?.lifecycle;
  return f?.lifecycles.find((l) => l.key === lc);
}

export function stageName(f: Fields | null, contactClass: string | null | undefined, stage: string | null): string {
  if (!stage) return NO_STAGE;
  return lifecycleOf(f, contactClass)?.stages.find((s) => s.key === stage)?.name
    ?? f?.lifecycles.flatMap((l) => l.stages).find((s) => s.key === stage)?.name ?? stage;
}

export const className = (f: Fields | null, key: string | null | undefined) => f?.classes.find((c) => c.key === key)?.name ?? key ?? '–';

export function gradeName(f: Fields | null, key: string | null | undefined): string {
  if (!key) return 'No grade';
  const g = f?.grades.find((x) => x.key === key);
  return g && g.label !== g.key ? `${g.key} · ${g.label}` : key;
}

// Every stage key once, for a filter, in lifecycle order; a key two lifecycles name differently shows both names.
export function stageChoices(f: Fields): Array<[string, string]> {
  const seen = new Map<string, string[]>();
  for (const l of f.lifecycles) for (const s of l.stages) { const names = seen.get(s.key) ?? []; if (!names.includes(s.name)) names.push(s.name); seen.set(s.key, names); }
  return [...seen].map(([k, names]) => [k, names.join(' / ')]);
}

// A key made from a name when something is added; it never changes after.
export function keyFrom(name: string, taken: string[], grade = false): string {
  const base = grade
    ? name.trim().replace(/[^A-Za-z0-9_+-]+/g, '').slice(0, 20) || 'G'
    : name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 36) || 'item';
  let key = base;
  for (let n = 2; taken.includes(key); n++) key = `${base.slice(0, grade ? 17 : 36)}${grade ? '' : '_'}${n}`;
  return key;
}
